import { Rating } from "@/lib/Components/Rating/Rating";
import { MessageSquare, Calendar, ExternalLink } from "lucide-react";
import dayjs from "dayjs";
import { Link } from "@tanstack/react-router";

import {
  Pagination,
  PaginationPrevious,
  PaginationNext,
  PaginationList,
  PaginationPage,
  PaginationGap,
} from "@/components/catalyst/pagination";
import { proDriverReviewListQuery } from "@/app/ProDrivers/WorkOrders/GraphQL/proDriverReviewListQuery";
import { Avatar } from "@/components/catalyst/avatar";
import { getInitials } from "@/lib/Utils/getInitials";
import { useState } from "react";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";

// Helper function to generate page numbers with gaps
function getPageNumbers(
  currentPage: number,
  lastPage: number,
): (number | null)[] {
  if (lastPage <= 7) {
    return Array.from({ length: lastPage }, (_, i) => i + 1);
  }

  const pages: (number | null)[] = [];

  if (currentPage <= 4) {
    for (let i = 1; i <= 5; i++) {
      pages.push(i);
    }
    pages.push(null); // gap
    pages.push(lastPage);
  } else if (currentPage >= lastPage - 3) {
    pages.push(1);
    pages.push(null); // gap
    for (let i = lastPage - 4; i <= lastPage; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);
    pages.push(null); // gap
    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
      pages.push(i);
    }
    pages.push(null); // gap
    pages.push(lastPage);
  }

  return pages;
}

export function ProDriverReviewList({ proDriverId }: { proDriverId: string }) {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { data } = useSuspenseGqlQuery(proDriverReviewListQuery, {
    proDriverId,
    first: pageSize,
    page: currentPage,
  });

  const { hasMorePages, total, lastPage } = data.proDriverReviews.paginatorInfo;

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (hasMorePages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (data.proDriverReviews.data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <MessageSquare className="mb-4 h-12 w-12 text-gray-400" />
        <h3 className="mb-2 text-lg font-medium text-gray-900">
          No Reviews Yet
        </h3>
        <p className="max-w-sm text-gray-500">
          {"This driver hasn't received any reviews yet."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Reviews List */}
      <div className="divide-y divide-gray-200">
        {data.proDriverReviews.data.map((review) => {
          const workOrder = review.workOrderAssignment?.workOrder;
          return (
            <div
              key={review.id}
              className="px-6 py-5 transition-colors hover:bg-gray-50"
            >
              {/* Rating and Date Header */}
              <div className="mb-3 flex items-start justify-between">
                <Rating rating={review.rating} readonly size="sm" />
                <div className="flex items-center text-sm text-gray-500">
                  <Calendar className="mr-1 h-3.5 w-3.5" />
                  {dayjs(review.created_at).fromNow()}
                </div>
              </div>

              {/* Review Comment */}
              {review.comment ? (
                <p className="mb-4 leading-relaxed text-gray-700">
                  {review.comment}
                </p>
              ) : null}

              {/* Reviewer Info */}
              <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                <div className="flex items-center gap-3">
                  <Avatar
                    className="h-10 w-10 shadow-sm ring-2 ring-white"
                    media={review.reviewedBy.profilePicture}
                    initials={getInitials(review.reviewedBy?.name ?? "--")}
                  />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900">
                      {review.reviewedBy.name}
                    </span>
                    <span className="text-xs text-gray-500">Reviewer</span>
                  </div>
                </div>
                {workOrder ? (
                  <Link
                    from="/$tenant/$orgId/supplier/{-$supplierId}"
                    to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id"
                    params={(prev) => ({
                      ...prev,
                      id: workOrder.id,
                    })}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {workOrder.reference}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {lastPage > 1 && (
        <div className="flex items-center justify-between rounded-b-lg border-t border-gray-200 bg-gray-50 px-6 py-4">
          <div className="text-sm text-gray-700">
            Showing{" "}
            <span className="font-medium">
              {(currentPage - 1) * pageSize + 1}
            </span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(currentPage * pageSize, total)}
            </span>{" "}
            of <span className="font-medium">{total}</span> reviews
          </div>

          <Pagination>
            <PaginationPrevious
              onClick={handlePrevious}
              disabled={currentPage === 1}
              className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            />

            <PaginationList>
              {getPageNumbers(currentPage, lastPage).map((pageNum, index) =>
                typeof pageNum === "number" ? (
                  <span
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className="cursor-pointer"
                  >
                    <PaginationPage current={pageNum === currentPage}>
                      {pageNum}
                    </PaginationPage>
                  </span>
                ) : (
                  <PaginationGap key={`gap-${index}`} />
                ),
              )}
            </PaginationList>

            <PaginationNext
              onClick={handleNext}
              disabled={!hasMorePages}
              className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            />
          </Pagination>
        </div>
      )}
    </div>
  );
}
